import { useParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { loadScript } from "../../../../globals/constants";
import { serviceData } from "../../../../globals/serviceDetail";
import ItodoImage from "../../../elements/itodo-img";

function ServicesDetailPage() {
  const { serviceId } = useParams();

  // Determine previous and next service keys

  const service = serviceData[serviceId] || {};

  // Ref for script loading
  const scriptLoadedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function ensureScript() {
      if (!scriptLoadedRef.current) {
        try {
          if (mounted) setIsLoading(true);
          await loadScript("js/custom.js");
          scriptLoadedRef.current = true;
        } catch (error) {
          console.error("Failed to load custom.js:", error);
        } finally {
          if (mounted) setIsLoading(false);
        }
      }
    }
    ensureScript();
    return () => { mounted = false; };
  }, []);

  // Get other services for related services section
  const otherServices = Object.entries(serviceData)
    .filter(([key]) => key !== serviceId)
    .slice(0, 4);

  return (
    <>
      {/* Loader overlay shown while external script or heavy content is loading */}
      {isLoading && (
        <div className="site-loader-overlay" aria-hidden="true">
          <div className="loading" role="status" aria-live="polite">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
      {/* <Banner _data={dynamicBannerData} /> */}

      {/* Hero Section */}
      <div className="service-hero p-t80 p-b80" style={{ background: "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/assets/images/banner/services.jpg')", backgroundSize: 'cover', backgroundPosition: 'center', color: '#fff' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h1 className="service-title mb-4" style={{ fontFamily: "var(--app-font)", fontWeight: "800", fontSize: "3.5rem", color: "#fff" }}>{service.title}</h1>
              <p className="service-description lead" style={{ fontFamily: "var(--app-font)", fontSize: "1.25rem", opacity: "0.9" }}>
                {service.shortDescription}
              </p>
            </div>
            <div className="col-lg-5">
              <div className="service-image-wrapper pulse-animation">
                <ItodoImage
                  src={service.image}
                  alt={service.title}
                  className="rounded-lg shadow-2xl"
                  style={{ borderRadius: "20px", border: "5px solid rgba(255,255,255,0.2)" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="service-content p-t80 p-b80 bg-white" style={{ fontFamily: "var(--app-font)" }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              {/* Overview Card */}
              <div className="content-card mb-5 p-r30">
                <h2 className="card-title mb-4" style={{ fontWeight: "700", color: "#222" }}>Service Overview</h2>
                <p className="overview-text" style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "#444" }}>{service.fullDescription}</p>

                {/* Key Services Grid */}
                {service.keyServices && service.keyServices.length > 0 && (
                  <div className="m-t50">
                    <h2 className="card-title mb-4" style={{ fontWeight: "700", color: "#222" }}>Key Features</h2>
                    <div className="row">
                      {service.keyServices.map((keyService, index) => (
                        <div key={index} className="col-md-6 mb-4">
                          <div className="feature-card p-4 h-100" style={{ background: "#f8f9fa", borderRadius: "12px", borderLeft: "4px solid #007DB6" }}>
                            <h4 className="feature-title mb-3" style={{ fontWeight: "600", fontSize: "1.2rem" }}>{keyService.name}</h4>
                            <p className="feature-description" style={{ fontSize: "0.95rem", color: "#555" }}>
                              {keyService.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="sticky-sidebar">
                {/* Benefits List */}
                {service.benefits && service.benefits.length > 0 && (
                  <div className="benefit-sidebar-card p-4 mb-4" style={{ background: "#007DB6", color: "#fff", borderRadius: "16px" }}>
                    <h3 className="mb-4" style={{ color: "#fff", fontSize: "1.5rem" }}>Key Benefits</h3>
                    <ul className="list-unstyled">
                      {service.benefits.map((benefit, index) => (
                        <li key={index} className="mb-3 d-flex align-items-start">
                          <i className="fa fa-check-circle mr-3 mt-1" style={{ fontSize: "1.2rem" }} />
                          <span style={{ fontSize: "1rem" }}>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Why Choose Sidebar */}
                {service.whyChoose && service.whyChoose.length > 0 && (
                  <div className="p-4" style={{ border: "1px solid #eee", borderRadius: "16px" }}>
                    <h4 className="mb-4">Why Partner With Us?</h4>
                    {service.whyChoose.map((reason, index) => (
                      <div key={index} className="mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <span className="badge badge-primary mr-2" style={{ background: "#007DB6" }}>0{index + 1}</span>
                          <span style={{ fontWeight: "600" }}>Reason {index + 1}</span>
                        </div>
                        <p className="small text-muted">{reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Services Section */}
      <div className="related-services p-t80 p-b80 bg-gray" style={{ fontFamily: "var(--app-font)" }}>
        <div className="container">
          <div className="section-head center">
            <div className="sx-head-s-title">Explore More</div>
            <div className="sx-head-l-title">
              <h2 className="sx-title">Related Solutions</h2>
            </div>
          </div>
          <div className="row">
            {otherServices.map(([serviceKey, relatedService]) => (
              <div key={serviceKey} className="col-lg-3 col-md-6 mb-4">
                <div className="card service-card h-100 shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
                  <div className="card-img-top overflow-hidden" style={{ height: "180px" }}>
                    <ItodoImage
                      src={relatedService.image || "images/services/pic-1.jpg"}
                      alt={relatedService.title}
                      className="img-fluid transition-all duration-500 hover:scale-110"
                      style={{ objectFit: "cover", height: "100%", width: "100%" }}
                    />
                  </div>
                  <div className="card-body p-4">
                    <h5 className="card-title" style={{ fontWeight: "600", minHeight: "3rem" }}>{relatedService.title}</h5>
                    <NavLink
                      to={`/services/detail/${serviceKey}`}
                      className="site-button-link text-primary"
                    >
                      Read More <i className="fa fa-angle-right" />
                    </NavLink>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default ServicesDetailPage;
